import VuetifySettings from '../models/VuetifySettings'
import VuetifyTheme from '../models/VuetifyTheme'
class SettingsService {
  public static getNested(obj, ...args) {
    return args.reduce((obj, level) => obj && obj[level], obj)
  }

  public static ApplyTheme(themeSettings) {
    const root = document.documentElement
    const theme = themeSettings.theme
    let themeValues = {}

    const updateTheme = (theme) => {
      Object.entries(theme).forEach(([key, value]) => document.body.style.setProperty(key, value as string))
    }

    const replaceThemeValue = (name: string) => {
      return SettingsService.getNested(theme, name) || SettingsService.getNested(theme, 'colors', name) || name
    }

    if (theme.fontSizes) {
      theme.fontSizes.forEach((size, index) => {
        const key = `--fontsizes-${index}`
        themeValues = {
          ...themeValues,
          [key]: size,
        }
      })
    }

    if (theme.fontWeights) {
      themeValues = {
        ...themeValues,
        ['--fontweights-body']: theme.fontWeights.body,
        ['--fontweights-bold']: theme.fontWeights.bold,
        ['--fontweights-heading']: theme.fontWeights.heading,
        ['--fontweights-medium']: theme.fontWeights.medium,
      }
    }

    if (theme.fonts) {
      themeValues = {
        ...themeValues,
        ['--fonts-heading']: theme.fonts.heading,
        ['--fonts-body']: theme.fonts.body,
        ['--fonts-monospace']: theme.fonts.monospace,
      }
    }

    if (theme.lineHeights) {
      themeValues = {
        ...themeValues,
        ['--line-heights--heading']: theme.lineHeights.heading,
        ['--line-heights--body']: theme.lineHeights.body,
      }
    }

    if (theme.text) {
      const textElements = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'body-1', 'body-2']

      const replaceFontSize = (sizeValue) => {
        const matches = sizeValue.match(/\[(.*?)\]/)
        if (matches) {
          const index = matches[1]
          return `${theme.fontSizes[index]}px`
        } else {
          return sizeValue
        }
      }

      const replaceFontWeight = (weightValue: string) => {
        if (weightValue.includes('fontWeights.')) {
          const key = weightValue.split('.')[1]
          return `${theme.fontWeights[key]}`
        } else {
          return weightValue
        }
      }

      const filteredElements = Object.keys(theme.text)
        .filter((key) => textElements.includes(key))
        .reduce((obj, key) => {
          obj[key] = theme.text[key]
          return obj
        }, {})

      for (const textElement in filteredElements) {
        if (textElement) {
          if (filteredElements[textElement].size) {
            const key = `--v-text-${textElement}--size`
            themeValues = {
              ...themeValues,
              [key]: replaceFontSize(filteredElements[textElement].size),
            }
          }

          if (filteredElements[textElement].weight) {
            const key = `--v-text-${textElement}--weight`
            themeValues = {
              ...themeValues,
              [key]: replaceFontWeight(filteredElements[textElement].weight),
            }
          }
        }
      }

      if (theme.text.heading) {
        themeValues = {
          ...themeValues,
          ['--text-heading--font-family']: theme.text.heading.fontFamily,
          ['--text-heading--line-height']: theme.text.heading.lineHeight,
          ['--text-heading--font-weight']: replaceFontWeight(theme.text.heading.fontWeight),
        }
      }
    }

    if (theme.colors) {
      const radii = theme.radii || 0
      const primaryColor: string = SettingsService.getNested(theme, 'colors', 'primary')
      const primaryBtnBgColor: string = SettingsService.getNested(theme, 'buttons', 'primary', 'bg') || primaryColor
      const primaryBtnTextColor: string =
        SettingsService.getNested(theme, 'buttons', 'primary', 'color') || theme.colors.text
      const primaryRadius = SettingsService.getNested(theme, 'buttons', 'primary', 'radius')
      const primaryBtnBorderRadius: string = primaryRadius || primaryRadius === 0 ? primaryRadius : radii
      const primarySettings = new VuetifySettings({
        name: 'primary',
        base: primaryColor,
        btnBgColor: replaceThemeValue(primaryBtnBgColor),
        btnTxtColor: replaceThemeValue(primaryBtnTextColor),
        btnBorderRadius: replaceThemeValue(primaryBtnBorderRadius),
      })

      const secondaryColor: string = SettingsService.getNested(theme, 'colors', 'secondary')
      const secondaryBtnBgColor: string =
        SettingsService.getNested(theme, 'buttons', 'secondary', 'bg') || secondaryColor
      const secondaryBtnTextColor =
        SettingsService.getNested(theme, 'buttons', 'secondary', 'color') ||
        theme.colors.btnTextColor ||
        theme.colors.text

      const secondaryRadius = SettingsService.getNested(theme, 'buttons', 'secondary', 'radius')
      const secondaryBtnBorderRadius: string = secondaryRadius || secondaryRadius === 0 ? secondaryRadius : radii
      const secondarySettings = new VuetifySettings({
        name: 'secondary',
        base: secondaryColor,
        btnBgColor: replaceThemeValue(secondaryBtnBgColor),
        btnTxtColor: replaceThemeValue(secondaryBtnTextColor),
        btnBorderRadius: replaceThemeValue(secondaryBtnBorderRadius),
      })

      const tertiaryColor: string = SettingsService.getNested(theme, 'colors', 'tertiary')
      const tertiarySettings = new VuetifySettings({
        name: 'tertiary',
        base: tertiaryColor,
      })

      const errorColor: string = SettingsService.getNested(theme, 'colors', 'error')
      const successColor: string = SettingsService.getNested(theme, 'colors', 'success')
      const warningColor: string = SettingsService.getNested(theme, 'colors', 'warning')

      const errorSettings = new VuetifySettings({ name: 'error', base: errorColor })
      const successSettings = new VuetifySettings({ name: 'success', base: successColor })
      const warningSettings = new VuetifySettings({ name: 'warning', base: warningColor })

      const vuetifyThemeSettings = new VuetifyTheme({
        primary: primarySettings,
        secondary: secondarySettings,
        tertiary: tertiarySettings,
        error: errorSettings,
        warning: warningSettings,
        success: successSettings,
        borderRadius: radii,
      })

      themeValues = {
        ...themeValues,
        ...vuetifyThemeSettings.cssProperties(),
      }
    }

    if (theme.components) {
      const addComponentSetting = (componentName, property, value) => {
        const key = `--${componentName}-${property}`
        themeValues = {
          ...themeValues,
          [key]: replaceThemeValue(value),
        }
      }

      Object.entries(theme.components).forEach(([componentName, componentSettings]) => {
        if (typeof componentSettings === 'object') {
          Object.entries(componentSettings as object).forEach(([setting, value]) => {
            addComponentSetting(componentName, setting, value)
          })
        }
      })
    }

    updateTheme(themeValues)
  }
}

export default SettingsService
